import { formatDate } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { AccountSelectionService, KeywordTrackingService, UserSelectionService } from "@front/m19-services";
import {
  EmptyStateComponent,
  IAlertComponent,
  IButtonDirective,
  IIconComponent,
  ISelectMenuImports,
} from "@front/m19-ui";
import { KeywordTrackerService } from "@m19-board/services/keyword-tracker.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { catchError, map, of, switchMap, take, tap } from "rxjs";
import { CsvExportService } from "../services/csv-export.service";
import {
  AccountMarketplace,
  BrandScore,
  BrandTimeline,
  KeywordTrackerConfig,
  Marketplace,
  ShareOfVoiceTimeline,
} from "@front/m19-api-client";
import { ColorUtils } from "@front/m19-utils";
import { TranslocoDirective } from "@jsverse/transloco";
import { SovMainTableComponent } from "@m19-board/shared/sov-main-table/sov-main-table.component";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { SovMainChartComponent } from "@m19-board/shared/sov-main-chart/sov-main-chart.component";
import { SovPieChartComponent } from "@m19-board/shared/sov-pie-chart/sov-pie-chart.component";
import { LoadingService } from "@m19-board/services/loading.service";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
// "All other brands" color for charts
export const otherBrandColor = "#cccccc";
export type SovBrandDateType = Map<string, Map<number, number>>;

@UntilDestroy()
@Component({
  selector: "app-share-of-voice",
  templateUrl: "./share-of-voice.component.html",
  styleUrls: ["./share-of-voice.component.scss"],
  imports: [
    TranslocoDirective,
    IButtonDirective,
    ISelectMenuImports,
    SovMainTableComponent,
    ExportButtonComponent,
    SovMainChartComponent,
    SovPieChartComponent,
    EmptyStateComponent,
    IAlertComponent,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareOfVoiceComponent implements OnInit {
  private accountSelectionService = inject(AccountSelectionService);
  private kwTrackerService = inject(KeywordTrackerService);
  private keywordTrackingService = inject(KeywordTrackingService);
  private userSelectionService = inject(UserSelectionService);
  private csvService = inject(CsvExportService);
  private loadingService = inject(LoadingService);

  private readonly destroyRef = inject(DestroyRef);
  kwDropdownOpen = false;

  allSearchTerms = signal<string[]>([]);
  focusedSearchTerm = signal<string | undefined>(undefined);
  searchQuery = signal("");
  sovByBrandDate = signal<SovBrandDateType>(new Map());
  brandColors = signal<Map<string, string>>(new Map());
  hoveredBrands?: string;
  brandSelection: string[] = [];
  snapshotDate = signal<Date | undefined>(undefined);
  noData = signal(false);
  isReadonly = toSignal(this.accountSelectionService.readOnlyMode$);
  isSearchTermError = signal(false);

  accountInfo?: { accountId: string; marketplace: Marketplace; orgId: number };

  filteredSearchTerms = computed(() => {
    const terms = this.allSearchTerms();
    const query = this.searchQuery().toLowerCase();
    if (!query) return terms;
    return terms.filter((term) => term.toLowerCase().includes(query));
  });

  loading = this.loadingService.loading;

  ngOnInit(): void {
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        tap((_) => this.loadingService.loadingOn()),
        switchMap((am: AccountMarketplace) => {
          this.accountInfo = {
            accountId: am.accountId,
            marketplace: am.marketplace,
            orgId: am.resourceOrganizationId!,
          };
          return this.keywordTrackingService.getKeywordTrackerConfig(
            am.accountId,
            am.marketplace,
            am.resourceOrganizationId!,
          );
        }),
        map((config: KeywordTrackerConfig[]) => config.map((c) => c.searchTerm!)),
        switchMap((searchTerms: string[]) => {
          if (searchTerms.length === 0) {
            this.noData.set(true);
            return of([]);
          }
          this.allSearchTerms.set(searchTerms);
          this.focusSearchTerm(searchTerms[0]);

          this.noData.set(false);

          return this.getSearchTermTimeline(this.focusedSearchTerm()!);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sovTimeline: ShareOfVoiceTimeline[]) => {
        this.loadingService.loadingOff();
        this.isSearchTermError.set(false);
        if (sovTimeline.length && sovTimeline[0].timeline) {
          this.sovByBrandDate.set(this.buildBrandSovByDate(sovTimeline[0].timeline.brandTimelines!));
        }
      });
    this.userSelectionService.dateRange$.pipe(untilDestroyed(this)).subscribe((range: string[]) => {
      this.snapshotDate.set(new Date(range[1]));
    });
  }

  private getSearchTermTimeline(s: string) {
    this.isSearchTermError.set(false);
    return this.keywordTrackingService
      .getSOVTimeline(this.accountInfo!.accountId, this.accountInfo!.marketplace, this.accountInfo!.orgId, s)
      .pipe(
        take(1),
        catchError((error) => {
          this.loadingService.loadingOff();
          this.isSearchTermError.set(true);
          return of([]);
        }),
      );
  }

  private buildBrandSovByDate(brandsTimeline: BrandTimeline[]) {
    const sovByBrandDate = new Map<string, Map<number, number>>();
    const brandColors = new Map(this.brandColors());

    brandsTimeline
      .filter((b: BrandTimeline) => b.brand !== "other")
      .forEach((b: BrandTimeline) => {
        brandColors.set(b.brand!, this.genBrandColor(b.brand!));
        const sovByDate: Map<number, number> = new Map();
        let lastTimestamp = 0;

        b.scores?.forEach((bScore: BrandScore) => {
          lastTimestamp += bScore.timestampInDay! * 24 * 60 * 60 * 1000;

          sovByDate.set(lastTimestamp, bScore.score!);
        });

        sovByBrandDate.set(b.brand!, sovByDate);
      });

    this.brandColors.set(brandColors);
    this.loadingService.loadingOff();
    return sovByBrandDate;
  }

  genBrandColor(brand: string): string {
    return ColorUtils.genColor(brand);
  }

  exportToCsv() {
    const dateRange = this.userSelectionService.getDateRangeStr();

    const dataArray = [];

    for (const [brand, dateSov] of this.sovByBrandDate()) {
      for (const [date, sov] of dateSov) {
        const toDate: Date = new Date(date);
        if (toDate > new Date(dateRange[0]) && toDate < new Date(dateRange[1])) {
          const formatted = formatDate(toDate, "shortDate", "en");
          dataArray.push({ brand: brand, date: formatted, "share of voice": sov });
        }
      }
    }

    this.csvService.exportCsv(
      {
        prefix: "share_of_voice_" + this.focusedSearchTerm(),
        dateRange: dateRange,
      },
      dataArray,
    );
  }

  setBrandSelection(b: string[]) {
    this.brandSelection = [...b];
  }

  addNewKeyword() {
    this.kwTrackerService.openAddKwTrackingModal(
      this.accountInfo!.accountId,
      this.accountInfo!.marketplace,
      this.accountInfo!.orgId,
      [],
      " First share of voice data will be available after 24h",
    );

    this.kwDropdownOpen = false;
  }

  focusSearchTerm(term: string | undefined) {
    if (term == null) return;
    this.loadingService.loadingOn();
    this.focusedSearchTerm.set(term);
    this.getSearchTermTimeline(term).subscribe((sovTimeline: ShareOfVoiceTimeline[]) => {
      if (!sovTimeline.length || !sovTimeline[0].timeline) {
        this.loadingService.loadingOff();
        return;
      }
      this.sovByBrandDate.set(this.buildBrandSovByDate(sovTimeline[0].timeline!.brandTimelines!));
      this.loadingService.loadingOff();
    });
  }
}
